import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Handle both old format (token + email params) and new format (combined invite param)
  let token, email;
  
  const inviteParam = searchParams.get('invite');
  if (inviteParam) {
    // New format: combined token
    try {
      const decodedPayload = atob(inviteParam);
      const payload = JSON.parse(decodedPayload);
      token = payload.token;
      email = payload.email;
    } catch (e) {
      console.error('Failed to decode invite parameter:', e);
    }
  } else {
    // Old format: separate parameters
    token = searchParams.get('token');
    email = searchParams.get('email');
  }

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://gigloader.preview.emergentagent.com';

  useEffect(() => {
    const validateInvitation = async () => {
      if (!token || !email) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/validate-invitation?token=${token}&email=${email}`);
        
        if (response.ok) {
          const data = await response.json();
          setValidation(data);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid or expired invitation');
        }
      } catch (err) {
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, email, API_BASE]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }

    setRegistering(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/register-invited-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: email,
          username: formData.username,
          password: formData.password,
          name: formData.name
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the JWT token
        localStorage.setItem('token', data.access_token);
        
        setSuccess('Account created successfully! Redirecting to your dashboard...');
        
        // Redirect to main app after 2 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
        
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="loading-spinner">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
            <p>Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !validation) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="error-container">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="error-icon">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <h2>Invalid Invitation</h2>
            <p>{error}</p>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.href = '/'}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <h1>
            <svg width="24" height="24" viewBox="0 0 3000 3000" fill="none" xmlns="http://www.w3.org/2000/svg" className="title-icon">
              <path d="M2754.9,411.93h-355.94v-121.75c0-114.74-93.34-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75h-742.43v-121.75c0-114.74-93.35-208.08-208.08-208.08h-111.58c-114.74,0-208.08,93.35-208.08,208.08v121.75H245.1c-39.01,0-70.74,31.74-70.74,70.74v2230.08c0,39.01,31.73,70.74,70.74,70.74h2509.79c39.01,0,70.74-31.73,70.74-70.74V482.67c0-39.01-31.73-70.74-70.74-70.74ZM809.12,883.99h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h742.43v122.49c0,114.74,93.34,208.08,208.08,208.08h111.58c114.74,0,208.08-93.34,208.08-208.08v-122.49h285.19v2088.6H315.84V553.42h285.19v122.49c0,114.74,93.34,208.08,208.08,208.08ZM2079.3,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6ZM809.12,223.59h111.58c36.72,0,66.6,29.88,66.6,66.6v385.72c0,36.72-29.88,66.6-66.6,66.6h-111.58c-36.72,0-66.6-29.88-66.6-66.6v-385.72c0-36.72,29.88-66.6,66.6-66.6Z" fill="currentColor"/>
              <path d="M546.01,2101.99c-23.29,38.99-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.62,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.95,30.25,147.75,30.25,41.28,0,82.58-6.56,122.75-19.49,71.76-23.1,134.22-64.72,180.62-120.36,12.18-14.6,23.21-30.14,32.79-46.18,23.29-39,38.12-80.68,44.08-123.89,1.43-10.38,2.34-21.02,2.71-31.63.24-6.82.24-13.68.02-20.39v-686.86l822.96-175.09-.6,518.7-.22,199.44c-2.75,25.98-11.51,51.48-26.05,75.82-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.36-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.14-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,25.43-8.19,51.44-12.45,77.31-12.67h1.04c.35-.02.7-.02,1.05-.02,20.54,0,40.77,2.6,60.14,7.73l43.96,11.65v-417.79c0-39.01-31.74-70.74-70.74-70.74s-70.74,31.73-70.74,70.74v258.72c-28.76,2.76-57.38,8.69-85.37,17.7-90.97,29.29-166.77,88.43-213.41,166.54-23.29,39-38.12,80.68-44.08,123.89-6.29,45.56-2.4,90.63,11.55,133.96,13.95,43.33,37.09,82.2,68.77,115.54,30.05,31.62,66.42,56.82,108.08,74.91,46.25,20.08,95.96,30.25,147.74,30.25,41.28,0,82.58-6.56,122.75-19.49,71.19-22.92,133.29-64.1,179.58-119.1,12.58-14.95,23.96-30.91,33.83-47.43,23.29-39,38.12-80.68,44.08-123.9.76-5.54,1.39-11.18,1.85-16.76,1.34-16.07,1.39-32.3.17-48.26l.87-764.92c.02-21.46-9.53-41.52-26.2-55.03-16.66-13.51-38.26-18.7-59.27-14.24l-964.54,205.21c-32.46,6.91-56.02,36.01-56.02,69.19v761.72c-1.32,29.36-10.34,58.22-26.8,85.78-29.01,48.58-77.06,85.66-135.3,104.41-25.95,8.35-52.57,12.59-79.12,12.59-44.44,0-86.08-11.69-120.42-33.81-34.02-21.91-58.15-52.57-69.77-88.66-31.62-98.22,37.75-208.73,154.63-246.36,26.16-8.42,52.87-12.69,79.4-12.69,2.89,0,6.04.07,9.62.21,21.41.85,42.3,4.52,62.08,10.93l45.78,14.82v-742.37l1130.62-252.84c38.07-8.51,62.11-46.41,53.6-84.48-8.51-38.06-46.41-62.1-84.48-53.6l-1185.92,265.21c-32.56,7.28-55.3,35.67-55.3,69.04v632.35c-33.24,2.04-66.36,8.31-98.75,18.74-90.98,29.29-166.77,88.43-213.42,166.54h0Z" fill="currentColor"/>
            </svg>
            Gig Manager
          </h1>
          <p>Join and start managing your gigs!</p>
          {validation && (
            <p>
              <strong>{validation.inviter_name}</strong> has invited you to join their musical network!
            </p>
          )}
        </div>

        {success ? (
          <div className="success-container">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="success-icon">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
            <h2>Welcome to Gig Manager!</h2>
            <p>{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                disabled
                className="disabled-input"
              />
              <small>This email was invited to join</small>
            </div>

            <div className="form-group">
              <label htmlFor="name">Full Name (Optional)</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Your full name"
                disabled={registering}
              />
            </div>

            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Choose a username"
                required
                disabled={registering}
                minLength="3"
              />
              <small>At least 3 characters, will be used to login</small>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Create a secure password"
                required
                disabled={registering}
                minLength="6"
              />
              <small>At least 6 characters</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                required
                disabled={registering}
              />
            </div>

            {error && (
              <div className="error-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary btn-full"
              disabled={registering}
            >
              {registering ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  Creating Account...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="23" y1="11" x2="17" y2="11"></line>
                  </svg>
                  Create Account & Join
                </>
              )}
            </button>

            <div className="register-footer">
              <p>
                Already have an account? 
                <a href="/" className="link"> Sign in here</a>
              </p>
            </div>
          </form>
        )}
      </div>

      <style jsx>{`
        .register-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .register-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          padding: 40px;
          width: 100%;
          max-width: 500px;
          border: 1px solid #e5e5e5;
        }

        .register-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .register-header h1 {
          margin: 0 0 10px 0;
          color: #1a1a1a;
          font-size: 28px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .title-icon {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }

        .register-header p {
          color: #4a4a4a;
          font-size: 16px;
          margin: 0;
        }

        .register-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .form-group label {
          font-weight: 600;
          color: #1a1a1a;
          font-size: 14px;
        }

        .form-group input {
          padding: 12px;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.2s;
          background: white;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4a4a4a;
          box-shadow: 0 0 0 1px #4a4a4a;
        }

        .form-group input.disabled-input {
          background: #f8f9fa;
          color: #6b7280;
          border-color: #e5e7eb;
        }

        .form-group small {
          color: #6b7280;
          font-size: 12px;
        }

        .btn {
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          text-decoration: none;
        }

        .btn-primary {
          background: #1a1a1a;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2d2d2d;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(26, 26, 26, 0.3);
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          transform: none;
        }

        .btn-full {
          width: 100%;
        }

        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #fecaca;
        }

        .loading-spinner {
          text-align: center;
          padding: 40px;
        }

        .loading-spinner p {
          margin-top: 15px;
          color: #6b7280;
        }

        .error-container, .success-container {
          text-align: center;
          padding: 40px 20px;
        }

        .error-icon {
          color: #dc2626;
          margin-bottom: 20px;
        }

        .success-icon {
          color: #059669;
          margin-bottom: 20px;
        }

        .error-container h2, .success-container h2 {
          margin: 0 0 15px 0;
          color: #1a1a1a;
          font-weight: 700;
        }

        .error-container p, .success-container p {
          color: #4a4a4a;
          margin-bottom: 25px;
        }

        .register-footer {
          text-align: center;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .register-footer p {
          color: #6b7280;
          margin: 0;
        }

        .link {
          color: #1a1a1a;
          text-decoration: none;
          font-weight: 600;
        }

        .link:hover {
          text-decoration: underline;
          color: #2d2d2d;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 600px) {
          .register-container {
            padding: 30px 20px;
            margin: 10px;
          }

          .register-header h1 {
            font-size: 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;